'use client';

import Markdown from "@/app/Markdown";
import { ChunkAST, ChunkDocContext, FootnoteDefinition } from "@/lib/parseMarkdown";
import { useEffect, useMemo, useRef, useState } from "react";
import { FootnoteReference } from "mdast";

type H2 = {
  offset: number;
  header: string;
  id: string;
};

const elementOrEnclosingTable = (element: HTMLElement) => {
  const closestTable = element.closest('table');
  if (closestTable) {
    return closestTable;
  }
  return element;
}

const highlightLinkedElements = (identifier: string, highlight: boolean) => {
  const reference = document.querySelector(`[for="sn-${identifier}"]`) as HTMLElement;
  const footnote = document.getElementById(`footnote-${identifier}`);

  if (reference) {
    if (highlight) {
      reference.classList.add('border-black/20');
    } else {
      reference.classList.remove('border-black/20');
    }
  }

  if (footnote) {
    if (highlight) {
      footnote.classList.add('border-black/20');
    } else {
      footnote.classList.remove('border-black/20');
    }
  }
};

const Sidenote = (
  footnotes: FootnoteDefinition[],
  activeMobileFootnote: string | null,
  setActiveMobileFootnote: (id: string | null) => void
) => {
  const FootnoteComponent = ({ footnoteReference }: { footnoteReference: FootnoteReference, docContext?: ChunkDocContext }) => {
    const definition = footnotes.find((f) => f.identifier === footnoteReference.identifier);
    if (!definition) {
      return null;
    }

    const handleFootnoteClick = (e: React.MouseEvent) => {
      e.preventDefault();
      setActiveMobileFootnote(
        activeMobileFootnote === footnoteReference.identifier ? null : footnoteReference.identifier
      );
    };

    return <>
      <label
        htmlFor={`sn-${footnoteReference.identifier}`}
        className="margin-toggle sidenote-number border-2 border-black/0 p-1/2 cursor-pointer"
        onClick={handleFootnoteClick}
        onMouseEnter={() => highlightLinkedElements(footnoteReference.identifier, true)}
        onMouseLeave={() => highlightLinkedElements(footnoteReference.identifier, false)}
      ></label>
      <input type="checkbox" id={`sn-${footnoteReference.identifier}`} className="margin-toggle" />
    </>;
  };

  FootnoteComponent.displayName = 'FootnoteComponent';
  return FootnoteComponent;
}

const setFootnotePositions = () => {
  const footnoteReferences = Array.from(document.querySelectorAll('label.sidenote-number')) as HTMLLabelElement[];
  const footnotes = Array.from(document.querySelectorAll('div[id^="footnote-"]')) as HTMLDivElement[];

  const footnotePositions = footnotes.map(footnote => {
    const reference = footnoteReferences.find(ref => ref.htmlFor.slice(3) === footnote.id.slice(9));
    const initialPosition = reference ? elementOrEnclosingTable(reference)?.offsetTop ?? 0 : 0;
    return { footnote, reference, position: initialPosition };
  });

  footnotePositions.sort((a, b) => a.position - b.position);

  let adjusted = true;
  while (adjusted) {
    adjusted = false;
    for (let i = 1; i < footnotePositions.length; i++) {
      const prev = footnotePositions[i - 1];
      const current = footnotePositions[i];
      const minAllowed = prev.position + prev.footnote.offsetHeight + 10;
      if (current.position < minAllowed) {
        current.position = minAllowed;
        adjusted = true;
      }
    }
  }

  footnotePositions.forEach(({ footnote, reference, position }) => {
    const enclosingDetails = reference?.closest('details');
    footnote.style.top = `${position}px`;

    if (reference && !enclosingDetails) {
      footnote.classList.add('hidden', 'md:inline-block')
      return footnote.classList.remove('invisible');
    }
    if (enclosingDetails && !enclosingDetails.open) {
      return footnote.classList.add('hidden', 'md:hidden');
    }
    if (enclosingDetails && enclosingDetails.open) {
      return footnote.classList.remove('md:hidden', 'invisible');
    }
  });
}

const positionFootnotesAndTrackImages = () => {
  const images = Array.from(document.querySelectorAll('img'));

  setFootnotePositions();

  if (images.length === 0) {
    return;
  }

  // Add listeners to reposition footnotes whenever any image loads
  images.forEach(img => {
    if (!img.complete) {
      img.addEventListener('load', setFootnotePositions);
      img.addEventListener('error', setFootnotePositions);
    }
  });
}

export default function EssayPage({ nodes, toc = true, pageClassName, children }: { nodes: ChunkAST[], toc?: boolean, pageClassName?: string, children?: React.ReactNode }) {
  const [scrollPosition, setScrollPosition] = useState(0);
  const mainRef = useRef<HTMLDivElement>(null);
  const [h2s, setH2s] = useState<H2[]>([]);
  const [activeMobileFootnote, setActiveMobileFootnote] = useState<string | null>(null);

  const footnotes: FootnoteDefinition[] = useMemo(() => nodes.length > 0 ? nodes[0].docContext.footnotes : [], [nodes]);

  useEffect(() => {
    const mainEl = mainRef.current;
    const mainElHeight = mainEl?.offsetHeight || 0;
    const mainElTop = mainEl?.offsetTop || 0;
    const handleScroll = () => {
      setScrollPosition((window.scrollY - mainElTop) / mainElHeight);
    };
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      setScrollPosition(0);
    }
  }, []);

  useEffect(() => {
    const allH2s = mainRef.current?.querySelectorAll(':scope > div > h2') as NodeListOf<HTMLHeadingElement>;
    if (!allH2s) return
    const h2s = Array.from(allH2s).slice(1).map(h2 => ({
      offset: (h2.offsetTop - (h2.parentElement?.offsetTop || 0)) / (h2.parentElement?.offsetHeight || 1),
      header: h2.textContent || '',
      id: h2.id
    }));
    setH2s(h2s);
  }, []);

  useEffect(() => {
    positionFootnotesAndTrackImages();
    window.addEventListener('resize', setFootnotePositions);

    return () => {
      window.removeEventListener('resize', setFootnotePositions);
    }
  }, []);

  useEffect(() => {
    const detailsElements = mainRef.current?.querySelectorAll('details');
    if (!detailsElements) return

    const handleDetailsToggle = () => {
      setTimeout(setFootnotePositions, 10);
    };

    detailsElements.forEach(details => {
      details.addEventListener('toggle', handleDetailsToggle);
    });

    return () => {
      detailsElements.forEach(details => {
        details.removeEventListener('toggle', handleDetailsToggle);
      });
    };
  }, []);

  useEffect(() => {
    // Handle ESC key to close mobile footnote
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setActiveMobileFootnote(null);
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, []);

  return <>

    {toc && (
      <div className="[grid-area:toc] hidden md:block">
        <nav className="section-nav sticky left-2 top-2 group opacity">
          <div className="md:flex flex-col h-[100vh] pb-[26px] hidden">
            <svg width="130" style={{ height: 'calc(100vh - 4px)' }} viewBox={`0 0 130`} fill="none" xmlns="http://www.w3.org/2000/svg" className="absolute top-0 transition-opacity duration-1000">
              <line x1="3" y1="8" x2="3" y2={`${Math.max(scrollPosition * 100, 2)}%`} stroke="currentColor" strokeWidth="2" />
              {h2s.map(({ offset, header, id }) => <>
                <svg key={id} x={0} y={`${offset * 97}%`}>
                  <circle cx="3" cy="6" r="3" fill={'black'} />
                  <circle cx="3" cy="6" r="2" fill={offset <= scrollPosition ? "transparent" : "white"} />
                  <foreignObject x={12} y={-6} width={100} height={20}>
                    <div className="w-[100px] whitespace-nowrap overflow-hidden text-ellipsis opacity-0 group-hover:opacity-100 transition-opacity duration-300 cursor-pointer min-[1601px]:opacity-100">
                      <a className="text-black no-underline" href={`#${id}`}>{header}</a>
                    </div>
                  </foreignObject>
                </svg>
              </>)}
            </svg>
          </div>
        </nav>
      </div>
    )}
    
    <main className={`max-w-none [grid-area:content] mt-12 ${pageClassName}`} ref={mainRef}>
      <div className="max-w-[800px] ml-auto mr-auto">
        <Markdown
          nodes={nodes.flatMap(chunk => chunk.nodes)}
          FootnoteHandler={Sidenote(footnotes, activeMobileFootnote, setActiveMobileFootnote)}
        />
        {children}
      </div>

      {activeMobileFootnote && (
        <div className="fixed bottom-0 left-0 right-0 bg-white p-4 border-t border-gray-300 z-50 md:hidden">
          <div className="flex justify-between items-center mb-2">
            <span className="font-bold">Footnote {activeMobileFootnote}</span>
            <button
              className="text-gray-500 hover:text-gray-700"
              onClick={() => setActiveMobileFootnote(null)}
            >
              Close
            </button>
          </div>
          <div className="footnote-mobile-content max-h-[40vh] overflow-y-auto">
            {footnotes.find(f => f.identifier === activeMobileFootnote)?.children && (
              <Markdown
                key={`footnote-${activeMobileFootnote}`}
                nodes={footnotes.find(f => f.identifier === activeMobileFootnote)?.children || []}
                FootnoteHandler={Sidenote(footnotes, activeMobileFootnote, setActiveMobileFootnote)}
              />
            )}
          </div>
        </div>
      )}
    </main>
    <div className="[grid-area:graph]">
      {footnotes.map(({ identifier, children }) => <>
        <div
          id={`footnote-${identifier}`}
          key={`footnote-${identifier}`}
          className="essay-footnote gap-2.5 absolute w-1/4 invisible transition-all duration-200 border-2 border-black/0 p-2 text-xs"
          onMouseEnter={() => {
            highlightLinkedElements(identifier, true);
          }}
          onMouseLeave={() => {
            highlightLinkedElements(identifier, false);
          }}
        >
          <span className="m-0 float-left mr-1 text-sm pt-[1px]">{identifier}.</span>
          <Markdown nodes={children} FootnoteHandler={Sidenote(footnotes, activeMobileFootnote, setActiveMobileFootnote)} />
        </div>
      </>)}
    </div>
  </>
}